/**
 * SessionStatusBadge — small inline pill announcing the session lifecycle
 * state. One per card; updates over `/ws/cards/:cardId/io` `status` frames.
 *
 * The badge uses the status-taxonomy palette (`--status-*-fg`) for the
 * dot color, and never paints a saturated chrome — only a faint
 * `bg-muted` wash so the pill reads as data, not decoration. The
 * waking state pulses a thin spinner; active state fades the dot in
 * and out subtly.
 *
 * Founder mandate (ADR-057 / UI_GUIDELINES_v1 §2): no indigo / violet /
 * blue background for chrome. The only colour here lives on the dot.
 */
import type { SessionState } from '@chaos/types/session';
import { Loader2 } from 'lucide-react';

import { cn } from '../../lib/utils';

interface SessionStatusBadgeProps {
  state: SessionState;
  /** When true (default), the badge renders the human-readable label.
   * Set to false in dense rows (e.g. board card) to render just the dot. */
  withLabel?: boolean;
  className?: string;
}

interface StatusVisual {
  /** Reads from tokens.css — never inline a HSL literal. */
  dotVar: string;
  label: string;
  /** Should the dot pulse softly? */
  pulse: boolean;
  /** Should we render the spinner instead of a dot? */
  spinner: boolean;
}

const VISUALS: Record<SessionState, StatusVisual> = {
  created: { dotVar: '--status-gray-fg', label: 'Not started', pulse: false, spinner: false },
  active: { dotVar: '--status-green-fg', label: 'Active', pulse: true, spinner: false },
  idle: { dotVar: '--status-blue-fg', label: 'Idle', pulse: false, spinner: false },
  sleeping: { dotVar: '--status-yellow-fg', label: 'Paused', pulse: false, spinner: false },
  waking: { dotVar: '--status-orange-fg', label: 'Resuming', pulse: false, spinner: true },
  error: { dotVar: '--color-error', label: 'Error', pulse: false, spinner: false },
  dead: { dotVar: '--status-gray-fg', label: 'Ended', pulse: false, spinner: false },
};

export function SessionStatusBadge({
  state,
  withLabel = true,
  className,
}: SessionStatusBadgeProps) {
  const visual = VISUALS[state];

  return (
    <span
      role="status"
      className={cn(
        'inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-[11px] font-medium',
        'bg-muted/60 text-muted-foreground',
        className,
      )}
      data-state={state}
      aria-live="polite"
      aria-label={`Session status: ${visual.label}`}
    >
      {visual.spinner ? (
        <Loader2
          aria-hidden
          className="h-3 w-3 animate-spin"
          style={{ color: `hsl(var(${visual.dotVar}))` }}
        />
      ) : (
        <span
          aria-hidden
          className={cn('h-1.5 w-1.5 rounded-full', visual.pulse && 'animate-soft-pulse')}
          style={{ backgroundColor: `hsl(var(${visual.dotVar}))` }}
        />
      )}
      {withLabel && <span>{visual.label}</span>}
    </span>
  );
}
